import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { api } from "../../../singleton/axios";
import { onAppResume } from "../../../lib/platform";
import { RecentNotifications } from "../types";
import { useInboxEnabled } from "./useInboxEnabled";

export const RECENT_NOTIFICATIONS_QUERY_KEY = ["notifications", "recent"] as const;

// The server allows 12 requests a minute per user. Never poll faster.
const POLL_INTERVAL_MS = 60_000;

const EMPTY: RecentNotifications = { items: [], count: 0 };

/**
 * What is new: the New items AND the badge count, in one request, so every
 * consumer (avatar badge, burger dot, More tab dot, menu count) shares a
 * single cached query and a single poll.
 */
export const useRecentNotifications = () => {
  const queryClient = useQueryClient();
  const enabled = useInboxEnabled();

  // Native has no window focus event worth the name; coming back to the
  // foreground is its equivalent. cancelRefetch:false because several
  // consumers mount this hook — the first resume callback starts the fetch and
  // the rest join it, instead of each cancelling and re-issuing the request.
  useEffect(() => {
    if (!enabled) return undefined;
    return onAppResume(() => {
      void queryClient.invalidateQueries(
        { queryKey: RECENT_NOTIFICATIONS_QUERY_KEY },
        { cancelRefetch: false },
      );
    });
  }, [enabled, queryClient]);

  return useQuery({
    queryKey: RECENT_NOTIFICATIONS_QUERY_KEY,
    queryFn: async (): Promise<RecentNotifications> => {
      try {
        const { data } = await api.get<RecentNotifications>("/notifications/recent");
        return data;
      } catch (error) {
        // Rate limited: keep showing what we had. A badge is not worth an
        // error state, and the next poll will catch up.
        if (isAxiosError(error) && error.response?.status === 429) {
          return (
            queryClient.getQueryData<RecentNotifications>(RECENT_NOTIFICATIONS_QUERY_KEY) ?? EMPTY
          );
        }
        throw error;
      }
    },
    enabled,
    refetchInterval: POLL_INTERVAL_MS,
    // The app default is off (index.tsx); this is the one query where coming
    // back to the tab should show what arrived meanwhile.
    refetchOnWindowFocus: true,
    retry: false,
  });
};
